import React, { useEffect, useRef } from 'react';
import { CGProduct } from '../../types/shopify';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/ArrowForward';
import clxs from 'classnames';
import CardMaster from '../Product/CardMaster';
import useTheme from '@mui/material/styles/useTheme';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { useRedeemCheck } from 'utilities/hook';
import { cgtag } from 'utilities/gtag';
// import debug from 'debug';
// const d = debug('productHeroCard');

const useStyles = makeStyles(
  (theme: Theme) => ({
    cardContainer: {
      margin: '0 auto',
      paddingTop: '3vh',
    },
    container: {
      margin: '0 auto',
    },
    cardContainerBackground: {
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
    },
    cardContent: {
      display: 'flex',
      flexFlow: 'column',
      alignItems: 'center',
    },
    card: {
      width: 524,
      [theme.breakpoints.down('lg')]: {
        width: 350,
      },
      [theme.breakpoints.down('md')]: {
        width: 256,
      },
      perspective: 120,
    },
    cardInfo: {
      maxWidth: '100%',
      display: 'flex',
      flexFlow: 'column',
      alignItems: 'center',
      overflowY: 'hidden',
    },
    cardInfoHidden: {
      visibility: 'hidden',
    },
    button: {
      marginTop: theme.spacing(3),
      textAlign: 'center',
    },
    description: {
      marginTop: theme.spacing(3),
      maxWidth: '100%',
    },
  }),
  { name: 'ProductHeroCard' },
);

interface ProductHeroCardProps {
  item: CGProduct;
  idx: number;
  activeIdx: number;
  selected: boolean;
  mode?: 'selectCard' | 'viewCard' | 'viewCardSimple';
  noPadding?: boolean;
  cardValue?: number;
  viewButtonLabel?: string;
  click: (index: number) => void;
}

const ProductHeroCard: React.FC<ProductHeroCardProps> = ({ item, mode = 'viewCard', idx, activeIdx, selected, click, noPadding = false, children, cardValue, viewButtonLabel = 'Discover' }) => {
  const classes = useStyles();
  const cardRef = useRef<any>(null);
  const history = useHistory();
  // d('ProductHeroCard', item);
  const theme = useTheme();
  let cardSize: 'small' | 'medium' | 'large' = 'large';
  if (useMediaQuery(theme.breakpoints.down('lg'))) cardSize = 'medium';
  if (useMediaQuery(theme.breakpoints.down('md'))) cardSize = 'small';

  //if coming from redeem
  const isFromRedeem = useRedeemCheck();
  const source = isFromRedeem ? '/redeem' : '';

  useEffect(() => {
    const currentCard = cardRef.current;
    let counter = 0;
    const updateRate = 1;
    const isTimeToUpdate = () => {
      return counter++ % updateRate === 0;
    };
    var mouse = {
      origin: false,
      _x: 0,
      _y: 0,
      x: 0,
      y: 0,
      updatePosition: function (event: any) {
        var e = event || window.event;
        this.x = e.clientX - this._x;
        this.y = (e.clientY - this._y) * -1;
      },
      setOrigin: function (e: any) {
        this.origin = true;
        this._x = Math.floor(window.innerWidth / 2);
        this._y = 918 / 2; //Math.floor(window.innerHeight / 2);
      },
      show: function () {
        return '(' + this.x + ', ' + this.y + ')';
      },
    };

    const mouseListener = (ev: MouseEvent) => {
      if (isTimeToUpdate()) {
        if (!mouse.origin) {
          mouse.setOrigin(ev);
        }
        mouse.updatePosition(ev);
        updateTransformStyle((mouse.y / currentCard.offsetHeight / 2).toFixed(2), (mouse.x / currentCard.offsetWidth / 2).toFixed(2));
      }
    };

    const updateTransformStyle = (x: any, y: any) => {
      var style = 'rotateX(' + x + 'deg) rotateY(' + y + 'deg)';
      if (currentCard) {
        currentCard.style.tranform = style;
        currentCard.style.webkitTransform = style;
        currentCard.style.mozTransform = style;
        currentCard.style.msTransform = style;
        currentCard.style.oTransform = style;
      }
    };
    if (selected) window.addEventListener('mousemove', mouseListener);
    return () => {
      window.removeEventListener('mousemove', mouseListener);
      if (currentCard) {
        currentCard.style.transform = null;
      }
    };
  }, [selected, cardRef]);

  const clickCard = () => {
    if (!selected) click(idx);
  };
  return (
    <div className={noPadding ? classes.container : classes.cardContainer}>
      <div className={classes.cardContent}>
        <div className={classes.card}>
          <CardMaster noButton={selected} item={item} ref={cardRef} size={cardSize} onClick={clickCard} />
        </div>
        {children}
        <div className={clxs(classes.cardInfo, selected ? null : classes.cardInfoHidden)}>
          {mode === 'viewCardSimple' && (
            <>
              <Button
                component={RouterLink} // It is a link...
                to={'/product/' + item.handle}
                size="large"
                color="secondary"
                variant="outlined"
                endIcon={<SendIcon />}
                className={classes.button}
                onClick={() => {
                  cgtag('carousel_card_click', {
                    index: idx,
                    mode: mode,
                    item_id: item.handle,
                  });
                }}
              >
                {viewButtonLabel}
              </Button>
            </>
          )}
          {mode === 'viewCard' && (
            <>
              <Button
                component={RouterLink} // It is a link...
                to={'/product/' + item.handle}
                size="large"
                color="secondary"
                variant="outlined"
                endIcon={<SendIcon />}
                className={classes.button}
                onClick={() => {
                  cgtag('carousel_card_click', {
                    index: idx,
                    mode: mode,
                    item_id: item.handle,
                  });
                }}
              >
                {viewButtonLabel} {item.title}
              </Button>
              <Typography align="center" className={classes.description}>
                {(item.pd?.abstract !== 'null' && item.pd?.abstract?.slice(0, 50)) || ''}
              </Typography>
            </>
          )}
          {mode === 'selectCard' && (
            <>
              <Button
                // component={RouterLink} // It is a link...
                onClick={() => {
                  cgtag('carousel_card_click', {
                    index: idx,
                    mode: mode,
                    item_id: item.handle,
                  });
                  // We don't want to use an <A> link as that will force the page to be spidered by
                  // the react-snap process... So we're using a history.push('') call to avoid that.
                  history.push(source + '/product/' + item.handle + '/configure');
                }}
                // to={source + '/product/' + item.handle + '/configure'}
                // replace // Using replace here so the history is managed...
                size="large"
                color="secondary"
                variant="outlined"
                endIcon={<SendIcon />}
                className={classes.button}
              >
                {viewButtonLabel ? viewButtonLabel : 'Select Card'}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductHeroCard;
